import axios from 'axios';
import moment from 'moment-timezone';
import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { SimpleFlexbox } from 'ctm-components';
import { pageContext, getQueryVariable } from 'ctm-util-functions';
import { updateState, updateTokens } from '../prefill/utils';
import { useStyles } from './postcode_confirmation_styles';
import { MechaInputField } from '../mecha_components/mecha_input_field/mecha_input_field';
import { MechaButton } from '../mecha_components/mecha_button/mecha_button';
import { MechaAlertBanner } from '../mecha_components/mecha_alert_banner/mecha_alert_banner';
import { convertObjectKeysToLowercase } from '../utils';

const _PostcodeConfirmation = (props) => {
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';
    const classes = useStyles({ isInvalid, isXs });
    const [lockoutTime, setLockoutTime] = useState(localStorage.getItem('lockoutTime_11Mre') || null);
    const [futureTimestamp, setFutureTimestamp] = useState(localStorage.getItem('futureTimestamp_11Mre') || null);
    const {
        setPostcode,
        setLoading,
        isLoading,
        setInvalid,
        isInvalid,
        setWasInvalid,
        wasInvalid,
        postcode11Month,
        accessToken,
        journeyId,
        productId,
        journeyUrl,
        mapToState,
        stageChangeIsEditable,
        serverConfigEnv,
        setHasSeenConfirmation,
        setPrefillJourney,
        trackPageView
    } = props;

    let postcodeLength = 0;
    if (typeof postcode11Month !== 'undefined') {
        postcodeLength = postcode11Month.length;
    }

    // Track page view event once user lands on the page
    useEffect(() => trackPageView(journeyId), []);

    let intervalId;

    // Check if the current time is equal to the future time
    // We useCallback to memoize the function and stop recreating it on every render
    const checkTimestamp = useCallback(() => {
        const currentTimestamp = moment().format('HH:mm');

        if (currentTimestamp >= futureTimestamp) {
            setLockoutTime(0);
            setInvalid(false);
            setWasInvalid(false);
            clearInterval(intervalId);
            setFutureTimestamp(null);
            localStorage.removeItem('futureTimestamp_11Mre');
            localStorage.removeItem('lockoutTime_11Mre');
        }
    }, [futureTimestamp]);

    useEffect(() => {
        // Set an interval to check the timestamp every minute
        if (futureTimestamp !== null) {
            intervalId = setInterval(checkTimestamp, 60000);
        }
    }, [futureTimestamp]);

    const handleClick = async (event) => {
        setLoading(true);
        const verifyEndpoint = `${journeyUrl}${serverConfigEnv === 'local' ? '' : '/'}journey/${journeyId}/verify`;
        await axios
            .post(
                verifyEndpoint,
                {
                    productId,
                    postcode: postcode11Month
                },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            )
            .then((response) => {
                setLoading(false);
                mapToState(response.data, accessToken);
                stageChangeIsEditable(response.data.isEditable);
                setHasSeenConfirmation();
                setPrefillJourney();
                pageContext.getProps().browserHistory.push('/car-insurance/journey/confirmation');

                return response;
            })
            .catch((err) => {
                // unsure of the format TYK gateway will respond with, so we will convert all header properties to lowercase
                const lowercaseHeader = convertObjectKeysToLowercase(err.response.headers);
                // if there are 429 errors, we need to set the lockout time
                if (err.response.status === 429) {
                    // We receive the time in a string format, we need to convert it to a Int so that we can convert it to minutes
                    const convertSecondsToMinutesLockoutTime = Math.round(parseInt(lowercaseHeader['x-ctm-gateway-remaining-lockout-time'], 10) / 60);
                    setLockoutTime(convertSecondsToMinutesLockoutTime);
                    const _currentTimestamp = moment().format('HH:mm');
                    const _futureTimestamp = moment(_currentTimestamp, 'HH:mm')
                        .add(convertSecondsToMinutesLockoutTime, 'minutes')
                        .format('HH:mm');

                    setFutureTimestamp(_futureTimestamp);
                    localStorage.setItem('lockoutTime_11Mre', convertSecondsToMinutesLockoutTime);
                    localStorage.setItem('futureTimestamp_11Mre', _futureTimestamp);
                }
                setInvalid(true);
                setWasInvalid(true);
                setLoading(false);
            });
    };

    const handleEnter = async (event) => {
        event.preventDefault();
        if (postcodeLength === 4 && !isLoading && !isInvalid) {
            handleClick(event);
        }
    };

    return (
        <form className={classes.formWrapper} data-id={'postcode_verification'}>
            <SimpleFlexbox className={classes.postcode_confirmation_container}>
                <MechaInputField
                    dataId={'postcode_verification_input'}
                    type="text"
                    value={postcode11Month}
                    inputId="postcode11Month"
                    maxlength={4}
                    minlength={4}
                    pattern="^[0-9]{4,4}$"
                    placeholder="Enter postcode"
                    loading={isLoading}
                    invalid={isInvalid}
                    handleChange={(event) => {
                        setPostcode(event, setInvalid);
                    }}
                    onEnter={(event) => handleEnter(event)}
                />
                {isInvalid && futureTimestamp === null ? (
                    <MechaAlertBanner
                        content="Sorry, the postcode you entered didn't match our records, try again or select ‘Start a new quote’."
                        dataId={'postcode_verification_error_banner'}
                    />
                ) : null}
                {futureTimestamp !== null ? (
                    <MechaAlertBanner
                        content={`Sorry, you've reached the maximum number of attempts. Please try again in
                                ${lockoutTime <= 0 ? '1' : lockoutTime}
                                ${lockoutTime <= 1 ? 'minute ' : 'minutes '}
                                or start a new quote.`}
                        justify={'left'}
                        dataId={'postcode_verification_error_banner'}
                    />
                ) : null}
                <div className={classes.postcode_button_wrapper}>
                    {futureTimestamp !== null ? (
                        <MechaButton
                            dataId={'max_attempts_postcode_verification_new_quote_button'}
                            buttonLabel="Start a new quote"
                            buttonSize="lg"
                            buttonType="link"
                            color="blue"
                            href={'/car-insurance/journey/start'}
                        />
                    ) : (
                        <MechaButton
                            dataId={'postcode_verification_confirm_button'}
                            buttonLabel="Confirm"
                            buttonSize="lg"
                            buttonType="hero"
                            isDisabled={isLoading || isInvalid || postcodeLength !== 4}
                            onClick={(event) => handleClick(event)}
                        />
                    )}
                    {wasInvalid && futureTimestamp === null ? (
                        <MechaButton
                            dataId={'postcode_verification_new_quote_button'}
                            buttonLabel="Start a new quote"
                            buttonSize="lg"
                            buttonType="link"
                            href={'/car-insurance/journey/start'}
                        />
                    ) : null}
                </div>
            </SimpleFlexbox>
        </form>
    );
};

const mapStateToProps = (state) => {
    const postcode11Month = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'postcode11Month', 'postcode11Month']);
    const isLoading = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'postcode11Month', 'loading']);
    const isInvalid = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'postcode11Month', 'invalid']);
    const wasInvalid = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'postcode11Month', 'wasInvalid']);
    const serverConfigEnv = state.getIn(['serverConfig', 'env']);

    const journeyUrl = state.getIn(['serverConfig', 'journeyServiceBaseUrl']);

    const journeyId = getQueryVariable('journey_id');
    const productId = getQueryVariable('product_id');
    const accessToken = getQueryVariable('access_token');

    return { postcode11Month, isLoading, isInvalid, wasInvalid, accessToken, journeyId, productId, journeyUrl, serverConfigEnv };
};

const mapDispatchToProps = (dispatch) => ({
    setPostcode: (value, setInvalid) => {
        setInvalid(false);
        dispatch({
            type: 'SET_FORM_VALUE',
            value,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.postcode11Month.postcode11Month'
        });
    },
    setLoading: (value) => {
        dispatch({
            type: 'SET_FORM_VALUE',
            value,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.postcode11Month.loading'
        });
    },
    setInvalid: (value) => {
        dispatch({
            type: 'SET_FORM_VALUE',
            value,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.postcode11Month.invalid'
        });
    },
    setWasInvalid: (value) => {
        dispatch({
            type: 'SET_FORM_VALUE',
            value,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.postcode11Month.wasInvalid'
        });
    },
    mapToState: (responsePayload, accessToken) => {
        updateTokens(dispatch, accessToken);
        updateState(dispatch, responsePayload);
        const currentDate = moment().format('YYYY-MM-DD');
        dispatch({
            type: 'SET_FORM_VALUE',
            identifier: 'coverDetail.commencementDate',
            stateKey: 'vertical.quote',
            value: moment(currentDate).isAfter(responsePayload.form.coverDetail.commencementDate) ? currentDate : responsePayload.form.coverDetail.commencementDate
        });
    },
    trackPageView: (journeyId) => {
        dispatch({
            type: 'ELEVEN_MONTH_VERIFY',
            journeyId
        });
    },
    stageChangeIsEditable: (isEditable) => {
        dispatch({ type: 'SET_VALUE', value: isEditable, stateKey: 'vertical.quote', identifier: 'isEditable' });
    },
    setHasSeenConfirmation: () => {
        dispatch({ type: 'SET_PAGE_VALUE', value: true, identifier: 'hasSeenConfirmation' });
    },
    setPrefillJourney: () => {
        // this property instructs the UI to return to confirmation page after editing
        dispatch({ type: 'SET_PAGE_VALUE', value: true, identifier: 'prefillJourney' });
    }
});

_PostcodeConfirmation.propTypes = {
    dispatch: PropTypes.func,
    setPostcode: PropTypes.func,
    accessToken: PropTypes.string,
    postcode11Month: PropTypes.string,
    journeyId: PropTypes.string,
    productId: PropTypes.string
};

const PostcodeConfirmation = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PostcodeConfirmation);

export { _PostcodeConfirmation, PostcodeConfirmation };
